import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import Button from "designV2/components/Button";
import AuthShell, {
  CodeInput,
  clearCodeTimer,
  formatSeconds,
  go,
  readCodeTimer,
  readableError,
  startCodeTimer,
  useCountdown,
} from "./auth/AuthShell";

const CHECK_VERIFICATION_CODE = gql`
  query checkShopAdminPhoneVerificationCode(
    $phoneNumber: String!
    $phoneVerificationCode: String!
  ) {
    checkShopAdminPhoneVerificationCode(
      input: { phoneNumber: $phoneNumber, phoneVerificationCode: $phoneVerificationCode }
    ) {
      phoneSignUpCode
    }
  }
`;

const RESEND_CODE = gql`
  mutation getShopAdminPhoneVerificationCode($phoneNumber: String!) {
    getShopAdminPhoneVerificationCode(phoneNumber: $phoneNumber) {
      phoneVerificationCodeExpireTime
    }
  }
`;

const RESEND_COOLDOWN_SECONDS = 30;

/**
 * EnterCode - confirm the phone number a new shop admin signed up with.
 *
 * The code lasts five minutes and dies after three wrong guesses, so
 * the screen shows the clock and can send a new one. Previously the
 * clock running out threw you back to the sign-in page mid-typing with
 * no way to ask for another code except starting over.
 */
const EnterCode = (props) => {
  const history = props.history;
  const phoneNumber = window.localStorage.getItem("phoneNumber");

  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [checking, setChecking] = useState(false);

  const [remaining, setRemaining] = useCountdown(readCodeTimer());
  const [resendIn, setResendIn] = useCountdown(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);

  const [checkCode, { data, error }] = useLazyQuery(CHECK_VERIFICATION_CODE, {
    fetchPolicy: "network-only",
  });
  const [resendCode] = useMutation(RESEND_CODE);

  // The old version navigated from inside the render body, which fired
  // on every re-render until the browser caught up with the first one.
  useEffect(() => {
    if (data && data.checkShopAdminPhoneVerificationCode) {
      window.localStorage.setItem(
        "signupCode",
        data.checkShopAdminPhoneVerificationCode.phoneSignUpCode
      );
      clearCodeTimer();
      go(history, "/signup");
    }
  }, [data, history]);

  useEffect(() => {
    if (error) {
      setChecking(false);
      setCodeError(readableError(error));
    }
  }, [error]);

  // Takes the code rather than reading state, so the auto-submit that
  // fires as the last digit lands does not send the previous value.
  const submit = (value) => {
    if (checking || remaining === 0) return;

    if (value.length !== 5) {
      setCodeError("Enter all five digits");
      return;
    }

    setCodeError("");
    setChecking(true);
    checkCode({ variables: { phoneNumber, phoneVerificationCode: value } });
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    submit(code);
  };

  const onResend = () => {
    if (resending || resendIn > 0) return;

    setResending(true);
    setCodeError("");
    resendCode({ variables: { phoneNumber } })
      .then((res) => {
        const seconds = Number(
          res.data.getShopAdminPhoneVerificationCode.phoneVerificationCodeExpireTime
        );
        startCodeTimer(seconds);
        setRemaining(seconds);
        setResendIn(RESEND_COOLDOWN_SECONDS);
        setCode("");
        setResending(false);
      })
      .catch((err) => {
        setResending(false);
        setCodeError(readableError(err));
      });
  };

  // Reaching this page without a phone number means the sign-up was
  // started in another tab or the session was cleared.
  if (!phoneNumber) {
    return (
      <AuthShell
        title="Start again"
        subtitle="We do not have a phone number to verify."
        currentStep={0}
      >
        <div className="authForm">
          <div className="dsFormMessage warning" role="alert">
            <ErrorOutlineIcon />
            <span>Your sign-up was interrupted. Enter your phone number again to continue.</span>
          </div>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => go(history, "/login?tab=signup")}
          >
            Back to sign up
          </Button>
        </div>
      </AuthShell>
    );
  }

  const expired = remaining === 0;

  return (
    <AuthShell
      title="Check your phone"
      subtitle="Enter the five-digit code we just sent you."
      currentStep={0}
    >
      <form className="authForm" onSubmit={onSubmit}>
        <div className="authRecap">
          <span className="authRecapValue">{phoneNumber}</span>
          <button
            type="button"
            className="authLink"
            onClick={() => go(history, "/login?tab=signup")}
          >
            Change
          </button>
        </div>

        {codeError && (
          <div className="dsFormMessage error" role="alert">
            <ErrorOutlineIcon />
            <span>{codeError}</span>
          </div>
        )}

        <CodeInput
          value={code}
          onChange={setCode}
          onComplete={(value) => {
            if (!expired) submit(value);
          }}
          error={Boolean(codeError)}
          disabled={checking || expired}
        />

        <div className="authCountdown">
          <span>
            {expired ? (
              <span className="authCountdownTime isExpired">Code expired</span>
            ) : (
              <React.Fragment>
                Expires in <span className="authCountdownTime">{formatSeconds(remaining)}</span>
              </React.Fragment>
            )}
          </span>
          <button
            type="button"
            className="authLink"
            onClick={onResend}
            disabled={resending || resendIn > 0}
          >
            {resending
              ? "Sending"
              : resendIn > 0
              ? `Resend in ${resendIn}s`
              : "Send a new code"}
          </button>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={checking}
          disabled={expired}
        >
          {checking ? "Checking" : "Continue"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default EnterCode;
